import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save } from "lucide-react";
import { api } from "@/lib/api";
import { useContent } from "@/context/ContentContext";

const input = "w-full rounded-lg border border-[#2C2C2C]/20 bg-white px-3 py-2 text-sm outline-none focus:border-[#C9A84C]";
const Section = ({ title, children }) => (
  <div className="rounded-xl border border-[#2C2C2C]/10 bg-white p-6">
    <h2 className="mb-4 font-serif text-xl text-[#2C2C2C]">{title}</h2>
    <div className="space-y-4">{children}</div>
  </div>
);
const Row = ({ label, children }) => (
  <div>
    <label className="mb-1 block text-sm font-medium text-[#2C2C2C]">{label}</label>
    {children}
  </div>
);

export default function AdminContenu() {
  const { refresh } = useContent();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/content").then((r) => setData(r.data)).catch(() => {}); }, []);

  if (!data) return <p className="text-[#2C2C2C]/60">Chargement…</p>;

  const upd = (path, value) => {
    setData((d) => {
      const copy = structuredClone(d);
      let node = copy;
      for (let i = 0; i < path.length - 1; i++) node = node[path[i]];
      node[path[path.length - 1]] = value;
      return copy;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/content", data);
      await refresh();
      toast.success("Contenu enregistré et publié");
    } catch { toast.error("Échec de l'enregistrement"); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="admin-contenu">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="font-serif text-3xl text-[#2C2C2C]">Contenu du site</h1>
        <button onClick={save} disabled={saving} data-testid="content-save" className="flex items-center gap-2 rounded-lg bg-[#2C2C2C] px-5 py-2.5 text-sm text-white hover:bg-[#C9A84C] hover:text-[#2C2C2C] disabled:opacity-60"><Save size={15} /> Enregistrer & publier</button>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <Section title="Section Hero (accueil)">
          <Row label="Titre"><input className={input} data-testid="content-hero-title" value={data.hero.title} onChange={(e) => upd(["hero", "title"], e.target.value)} /></Row>
          <Row label="Sous-titre"><textarea className={input} rows={3} value={data.hero.subtitle} onChange={(e) => upd(["hero", "subtitle"], e.target.value)} /></Row>
        </Section>

        <Section title="Chiffres clés">
          {data.stats.map((s, i) => (
            <div key={i} className="flex items-center gap-2">
              <input className={`${input} w-28`} value={s.value} onChange={(e) => { const arr = [...data.stats]; arr[i] = { ...arr[i], value: e.target.value }; upd(["stats"], arr); }} />
              <input className={input} value={s.label} onChange={(e) => { const arr = [...data.stats]; arr[i] = { ...arr[i], label: e.target.value }; upd(["stats"], arr); }} />
              <button onClick={() => upd(["stats"], data.stats.filter((_, j) => j !== i))} className="text-red-600"><Trash2 size={16} /></button>
            </div>
          ))}
          <button onClick={() => upd(["stats"], [...data.stats, { value: "", label: "" }])} className="flex items-center gap-1 text-sm text-[#C9A84C]"><Plus size={14} /> Ajouter un chiffre</button>
          <p className="text-xs text-[#2C2C2C]/50">Ne pas additionner automatiquement ces chiffres : certaines catégories peuvent se chevaucher. À vérifier avant publication.</p>
        </Section>

        <Section title="Zones d'intervention">
          <Row label="Traiteur"><textarea className={input} rows={2} value={data.zones.traiteur} onChange={(e) => upd(["zones", "traiteur"], e.target.value)} /></Row>
          <Row label="Cheffe à domicile"><textarea className={input} rows={2} value={data.zones.chef} onChange={(e) => upd(["zones", "chef"], e.target.value)} /></Row>
          <Row label="Coordination"><textarea className={input} rows={2} value={data.zones.coordination} onChange={(e) => upd(["zones", "coordination"], e.target.value)} /></Row>
        </Section>

        <Section title="Coordonnées & réseaux">
          <Row label="E-mail principal"><input className={input} value={data.contact.email_principal} onChange={(e) => upd(["contact", "email_principal"], e.target.value)} /></Row>
          <Row label="E-mail secondaire"><input className={input} value={data.contact.email_secondaire} onChange={(e) => upd(["contact", "email_secondaire"], e.target.value)} /></Row>
          <Row label="Téléphone"><input className={input} value={data.contact.phone} onChange={(e) => upd(["contact", "phone"], e.target.value)} /></Row>
          <Row label="Lien WhatsApp (optionnel)"><input className={input} placeholder="https://wa.me/33641283385" value={data.contact.whatsapp} onChange={(e) => upd(["contact", "whatsapp"], e.target.value)} /></Row>
          <Row label="Instagram"><input className={input} value={data.social.instagram} onChange={(e) => upd(["social", "instagram"], e.target.value)} /></Row>
          <Row label="Facebook"><input className={input} value={data.social.facebook} onChange={(e) => upd(["social", "facebook"], e.target.value)} /></Row>
          <Row label="LinkedIn"><input className={input} value={data.social.linkedin} onChange={(e) => upd(["social", "linkedin"], e.target.value)} /></Row>
        </Section>

        <Section title="À propos">
          <textarea className={input} rows={5} value={data.apropos} onChange={(e) => upd(["apropos"], e.target.value)} />
        </Section>

        <Section title="Documents juridiques">
          <Row label="Politique de confidentialité"><textarea className={input} rows={4} value={data.legal.confidentialite} onChange={(e) => upd(["legal", "confidentialite"], e.target.value)} /></Row>
          <p className="text-xs text-[#2C2C2C]/55">Mentions légales, CGV et conditions Cheffe : gérées dans la rubrique « Documents légaux ».</p>
        </Section>
      </div>
    </div>
  );
}
